import { Alert, Button, Input } from '@dittolive/anvil'
import { graphql } from 'babel-plugin-relay/macro'
import React, { useState } from 'react'
import { usePortalMutation } from 'utils'
import * as Yup from 'yup'

import { AppUserDestinationInputTransferMutation } from './__generated__/AppUserDestinationInputTransferMutation.graphql'
import TransferAppConfirmationModal from './TransferAppConfirmationModal'

const transferMutation = graphql`
  mutation AppUserDestinationInputTransferMutation(
    $appId: ID!
    $toEmail: String!
  ) {
    requestAppTransferToUser(appId: $appId, toEmail: $toEmail) {
      transferRequestDestination {
        ... on UserDestination {
          email
        }
      }
    }
  }
`

const validationSchema = Yup.string().email().required()

type Props = {
  appId: string
}

const AppUserDestinationInput: React.FC<Props> = ({ appId }) => {
  const [transferApp, isTransferringApp] =
    usePortalMutation<AppUserDestinationInputTransferMutation>(
      transferMutation,
      {
        mutationName: 'RequestAppTransferToUser',
        sentryContext: {
          component: 'AppUserDestinationInput',
        },
        showErrorToast: false,
      },
    )
  const [value, setValue] = useState('')
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)
  const [transferError, setTransferError] = useState<Error>()

  const handleEmailChanged = (evt: React.ChangeEvent<HTMLInputElement>) =>
    setValue(evt.target.value)

  const handleTransferClicked = () => {
    setTransferError(undefined)
    setIsConfirmationOpen(true)
  }

  const handleConfirmationClosed = (confirmTransfer: boolean) => {
    if (confirmTransfer) {
      transferApp({
        variables: { appId, toEmail: value },
        onCompleted: () => {
          setIsConfirmationOpen(false)
        },
        onError: (err) => {
          setTransferError(err)
        },
      })
    } else {
      setIsConfirmationOpen(false)
    }
  }

  const isInvalid = !validationSchema.isValidSync(value)

  return (
    <>
      {!!transferError && (
        <Alert
          autoDismiss={false}
          variant="danger"
        >{`Could not create the database transfer request: ${transferError.message}`}</Alert>
      )}

      <TransferAppConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={handleConfirmationClosed}
        appId={appId}
        destination={value}
        isProcessing={isTransferringApp}
      />

      <div className="max-w-xl">
        <div>
          <Input
            autoComplete="off"
            id="email"
            label="Email address"
            description="The email address of the transfer recipient"
            onChange={handleEmailChanged}
            placeholder="email@address.com"
            value={value}
          />
        </div>

        <Button
          disabled={isInvalid}
          onClick={handleTransferClicked}
          className="mt-8"
          variant="critical"
        >
          Transfer
        </Button>
      </div>
    </>
  )
}

export default AppUserDestinationInput
